import type { CallAnalysisPassMetric } from '@marnic/shared';
import { MiniSection } from '../primitives/MiniSection';

function formatCost(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`;
}
function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

export function PassMetricsTable({ metrics }: { metrics: Record<string, CallAnalysisPassMetric> | null }): JSX.Element | null {
  if (!metrics) return null;
  const entries = Object.entries(metrics);
  if (entries.length === 0) return null;

  const totalCost = entries.reduce((sum, [, m]) => sum + (m.costMicros ?? 0), 0);
  const totalTokens = entries.reduce(
    (sum, [, m]) => sum + (m.promptTokens ?? 0) + (m.completionTokens ?? 0),
    0,
  );

  return (
    <MiniSection
      title="Per-pass metrics"
      action={
        <span className="text-[10px] text-ink-500">
          total: {formatCost(totalCost)} · {totalTokens.toLocaleString()} tokens
        </span>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] tabular-nums">
          <thead className="text-ink-500 text-left">
            <tr>
              <th className="font-normal pr-2">Pass</th>
              <th className="font-normal pr-2">Status</th>
              <th className="font-normal pr-2">Tokens (in/out)</th>
              <th className="font-normal pr-2">Cost</th>
              <th className="font-normal pr-2">Duration</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([name, m]) => (
              <tr key={name}>
                <td className="capitalize pr-2">{name}</td>
                <td className={`pr-2 ${
                  m.status === 'failed' ? 'text-red-600'
                  : m.status === 'skipped' ? 'text-ink-500'
                  : 'text-emerald-700'
                }`}>{m.status}</td>
                <td className="pr-2 text-ink-700">
                  {m.promptTokens.toLocaleString()} / {m.completionTokens.toLocaleString()}
                </td>
                <td className="pr-2">{formatCost(m.costMicros ?? 0)}</td>
                <td className="pr-2">{formatDuration(m.durationMs ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {entries.some(([, m]) => m.error) && (
        <ul className="space-y-0.5 text-[11px] text-red-600">
          {entries
            .filter(([, m]) => m.error)
            .map(([name, m]) => (
              <li key={name}><span className="font-medium">{name}:</span> {m.error}</li>
            ))}
        </ul>
      )}
    </MiniSection>
  );
}
